<template>
  <div ref="rootEl" class="phone-input-wrapper" :data-testid="props.dataTestid">
    <div
      class="phone-input"
      :class="{ 'phone-input--error': displayError }"
    >
      <!-- Country selector -->
      <div class="phone-input__country" @click.stop>
        <UButton
          type="button"
          color="white"
          variant="ghost"
          class="phone-input__country-btn"
          @click="toggleDropdown"
        >
          <img
            class="phone-input__flag-img"
            :src="flagImageUrl(selectedCountry.code)"
            width="22"
            height="16"
            loading="lazy"
            decoding="async"
            :alt="selectedCountry.name"
          />
          <span class="phone-input__dial">{{ selectedCountry.dialCode }}</span>
          <span class="phone-input__chevron">▾</span>
        </UButton>
      </div>

      <div class="phone-input__divider"></div>

      <input
        ref="phoneNativeRef"
        data-testid="e2e-auth-phone-inner-input"
        :value="localNumber"
        type="tel"
        :placeholder="placeholder || maskedPlaceholder"
        autocomplete="tel-national"
        :disabled="disabled"
        class="phone-input__number-ui rounded-l-none rounded-r-hui-md h-full min-h-0 w-full min-w-0 border-0 bg-transparent px-4 text-base text-hui-foreground shadow-none ring-0 outline-none focus:ring-0 placeholder:text-hui-foreground/40"
        @input="onPhoneNativeInput"
        @blur="onPhoneBlur"
        @focus="onPhoneFocus"
      />

      <!-- Dropdown на всю ширину инпута (родитель .phone-input — relative) -->
      <div v-if="dropdownOpen" class="phone-input__dropdown" @click.stop>
        <input
          ref="searchInputCmpt"
          v-model="searchQuery"
          type="text"
          placeholder="Поиск страны..."
          class="phone-input__search"
          @click.stop
        />

        <ul class="phone-input__country-list">
          <li
            v-for="country in filteredCountries"
            :key="country.code"
            class="phone-input__country-item"
            :class="{ 'phone-input__country-item--active': country.code === selectedCountry.code }"
            @click.stop="selectCountry(country)"
          >
            <img
              class="phone-input__flag-img phone-input__flag-img--list"
              :src="flagImageUrl(country.code)"
              width="22"
              height="16"
              loading="lazy"
              decoding="async"
              :alt="country.name"
            />
            <span class="phone-input__country-name">{{ country.name }}</span>
            <span class="phone-input__dial-secondary">{{ country.dialCode }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div
      v-if="displayError"
      class="phone-input__error-wrap"
    >
      <IconTheErrorMas />
      <span class="text-xs text-hui-danger">
        {{ errors?.[0]?.$message ?? (typeof error === 'string' ? error : '') }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { parsePhoneNumber } from 'libphonenumber-js';

interface Country {
  code: string;
  name: string;
  dialCode: string;
  /** '#' — позиция цифры, остальные символы — разделители маски */
  mask: string;
}

const props = defineProps<{
  modelValue: string;
  label?: string;
  placeholder?: string;
  error?: boolean | string;
  errors?: Array<{ $message: string }>;
  showError?: boolean;
  disabled?: boolean;
  /** E2E: передать с родителя как data-testid="…" */
  dataTestid?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'change', value: string): void;
}>();

const COUNTRIES: Country[] = [
  { code: 'RU', name: 'Россия',        dialCode: '+7',   mask: '(###) ###-##-##' },
  { code: 'KZ', name: 'Казахстан',     dialCode: '+7',   mask: '(###) ###-##-##' },
  { code: 'BY', name: 'Беларусь',      dialCode: '+375', mask: '(##) ###-##-##'  },
  { code: 'UA', name: 'Украина',       dialCode: '+380', mask: '(##) ###-##-##'  },
  { code: 'UZ', name: 'Узбекистан',   dialCode: '+998', mask: '## ###-##-##'    },
  { code: 'AZ', name: 'Азербайджан',  dialCode: '+994', mask: '(##) ###-##-##'  },
  { code: 'AM', name: 'Армения',       dialCode: '+374', mask: '##-##-##-##'     },
  { code: 'GE', name: 'Грузия',        dialCode: '+995', mask: '(###) ##-##-##'  },
  { code: 'MD', name: 'Молдова',       dialCode: '+373', mask: '(##) ###-###'    },
  { code: 'KG', name: 'Кыргызстан',   dialCode: '+996', mask: '(###) ###-###'   },
  { code: 'TJ', name: 'Таджикистан',  dialCode: '+992', mask: '(###) ##-##-##'  },
  { code: 'TM', name: 'Туркменистан', dialCode: '+993', mask: '(##) ##-##-##'   },
  { code: 'DE', name: 'Германия',      dialCode: '+49',  mask: '#### #######'    },
  { code: 'GB', name: 'Великобритания',dialCode: '+44',  mask: '#### ######'     },
  { code: 'FR', name: 'Франция',       dialCode: '+33',  mask: '# ## ## ## ##'   },
  { code: 'IT', name: 'Италия',        dialCode: '+39',  mask: '### ### ####'    },
  { code: 'ES', name: 'Испания',       dialCode: '+34',  mask: '### ## ## ##'    },
  { code: 'TR', name: 'Турция',        dialCode: '+90',  mask: '(###) ### ## ##' },
  { code: 'US', name: 'США',           dialCode: '+1',   mask: '(###) ###-####'  },
  { code: 'CN', name: 'Китай',         dialCode: '+86',  mask: '### #### ####'   },
  { code: 'IL', name: 'Израиль',       dialCode: '+972', mask: '##-###-####'     },
  { code: 'AE', name: 'ОАЭ',           dialCode: '+971', mask: '## ### ####'     },
  { code: 'PL', name: 'Польша',        dialCode: '+48',  mask: '### ### ###'     },
  { code: 'CZ', name: 'Чехия',         dialCode: '+420', mask: '### ### ###'     },
  { code: 'LT', name: 'Литва',         dialCode: '+370', mask: '(##) ### ###'    },
  { code: 'LV', name: 'Латвия',        dialCode: '+371', mask: '### ## ###'      },
  { code: 'EE', name: 'Эстония',       dialCode: '+372', mask: '#### ####'       },
  { code: 'FI', name: 'Финляндия',     dialCode: '+358', mask: '## #######'      },
  { code: 'SE', name: 'Швеция',        dialCode: '+46',  mask: '##-### ## ##'    },
  { code: 'NO', name: 'Норвегия',      dialCode: '+47',  mask: '### ## ###'      },
];

// ── Mask helpers ──────────────────────────────────────────────────────────────

/** Оставляет только цифры */
const onlyDigits = (val: string): string => val.replaceAll(/\D/g, '');

/** Применяет маску к строке цифр. '#' — позиция цифры, остальное — разделители */
const applyMask = (digits: string, mask: string): string => {
  const maxDigits = [...mask].filter(c => c === '#').length;
  const limited = digits.slice(0, maxDigits);
  let result = '';
  let di = 0;
  for (const element of mask) {
    if (di >= limited.length) break;
    if (element === '#') {
      result += limited[di++];
    } else {
      result += element;
    }
  }
  return result;
};

/** Плейсхолдер из маски: '(###) ###-##-##' → '(000) 000-00-00' */
const maskToPlaceholder = (mask: string): string => mask.replaceAll('#', '0');

// ─────────────────────────────────────────────────────────────────────────────

const flagImageUrl = (isoCode: string) =>
  `https://flagcdn.com/w40/${isoCode.toLowerCase()}.png`;

const dropdownOpen = ref(false);
const searchQuery = ref('');
const searchInputCmpt = ref<HTMLInputElement | null>(null);
const phoneNativeRef = ref<HTMLInputElement | null>(null);
const rootEl = ref<HTMLElement | null>(null);

/** true — как только пользователь сфокусировался на поле номера */
const phoneFieldWasFocused = ref(false);

const hasValidationIssue = computed(
  () => Boolean(props.error) || ((props.errors?.length ?? 0) > 0),
);

/** Единые правила: рамка и текст; showError=false отключает оба */
const displayError = computed(() => {
  if (!hasValidationIssue.value) return false;
  if (props.showError === false) return false;
  if (props.showError === undefined) return true;
  return !!props.showError;
});

/**
 * Пытается определить страну по полному значению номера (с кодом страны).
 * Если значение пустое — возвращает первую страну в списке как дефолт.
 */
const resolveCountryFromValue = (val: string | undefined): Country => {
  const trimmed = (val ?? '').trim();
  if (!trimmed) return COUNTRIES[0];

  const compact = trimmed.replaceAll(/\s/g, '');
  const sorted = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);

  for (const c of sorted) {
    if (trimmed.startsWith(c.dialCode) || compact.startsWith(c.dialCode)) {
      // +7 — Россия по умолчанию; Казахстан выбирается только вручную
      if (c.dialCode === '+7') return COUNTRIES.find((x) => x.code === 'RU')!;
      return c;
    }
  }

  try {
    const parsed = parsePhoneNumber(trimmed);
    if (parsed?.country) {
      const found = COUNTRIES.find((c) => c.code === parsed.country);
      if (found) return found;
    }
  } catch {
    /* ignore */
  }

  return COUNTRIES[0];
};

const selectedCountry = ref<Country>(resolveCountryFromValue(props.modelValue));

/**
 * Извлекает локальную часть номера (без кода страны) и применяет маску.
 * full: "+7 9001234567" → "9001234567" → "(900) 123-45-67"
 */
const getLocalPart = (full: string, country: Country): string => {
  if (!full) return '';
  const dialDigits = onlyDigits(country.dialCode);
  const allDigits = onlyDigits(full);
  const localDigits = allDigits.startsWith(dialDigits)
    ? allDigits.slice(dialDigits.length)
    : allDigits;
  return applyMask(localDigits, country.mask);
};

const localNumber = ref(getLocalPart(props.modelValue, selectedCountry.value));

const maskedPlaceholder = computed(() => maskToPlaceholder(selectedCountry.value.mask));

const filteredCountries = computed(() => {
  if (!searchQuery.value) return COUNTRIES;
  const q = searchQuery.value.toLowerCase();
  return COUNTRIES.filter(
    c =>
      c.name.toLowerCase().includes(q) ||
      c.dialCode.includes(q) ||
      c.code.toLowerCase().includes(q),
  );
});

const focusSearchField = () => {
  nextTick(() => {
    searchInputCmpt.value?.focus();
  });
};

const focusPhoneField = () => {
  nextTick(() => {
    phoneNativeRef.value?.focus();
  });
};

const onPhoneNativeInput = (e: Event) => {
  const t = e.target as HTMLInputElement | null;
  if (t) onLocalNumberUpdate(t.value);
};

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value;
  if (dropdownOpen.value) {
    searchQuery.value = '';
    focusSearchField();
  }
};

const selectCountry = (country: Country) => {
  const currentDigits = onlyDigits(localNumber.value);
  selectedCountry.value = country;
  dropdownOpen.value = false;
  searchQuery.value = '';

  // Переприменяем введённые цифры к маске новой страны — номер не теряется
  const remasked = applyMask(currentDigits, country.mask);
  localNumber.value = remasked;
  emitValue(remasked);

  focusPhoneField();
};

const onLocalNumberUpdate = (val: string | number) => {
  const raw = String(val ?? '');
  const newDigits = onlyDigits(raw);

  /**
   * Определяем количество цифр, которые вмещает маска текущей страны.
   * Если пользователь вставил полный международный номер (начинается с + или 00,
   * либо цифр больше, чем позиций в маске), — пробуем разобрать его через
   * libphonenumber-js, получить nationalNumber и автоматически сменить страну.
   */
  const maxMaskDigits = [...selectedCountry.value.mask].filter(c => c === '#').length;
  const looksLikeFullNumber =
    raw.startsWith('+') ||
    raw.startsWith('00') ||
    newDigits.length > maxMaskDigits;

  if (looksLikeFullNumber && newDigits.length > 0) {
    const normalized = raw.startsWith('+')
      ? raw
      : raw.startsWith('00')
        ? raw.replace(/^00/, '+')
        : `+${newDigits}`;

    try {
      const parsed = parsePhoneNumber(normalized);
      if (parsed?.isValid()) {
        let country: Country | undefined;

        if (parsed.country) {
          country = COUNTRIES.find(c => c.code === parsed.country);
        }
        // Фолбэк по коду набора (например +7 → RU)
        if (!country) {
          country = COUNTRIES.find(c => c.dialCode === `+${parsed.countryCallingCode}`);
          // +7 — всегда Россия по умолчанию
          if (country?.dialCode === '+7') {
            country = COUNTRIES.find(c => c.code === 'RU');
          }
        }

        if (country) {
          selectedCountry.value = country;
          const masked = applyMask(parsed.nationalNumber, country.mask);
          localNumber.value = masked;
          emitValue(masked);
          return;
        }
      }
    } catch {
      /* не валидный номер — обрабатываем как обычный ввод */
    }
  }

  const existingDigits = onlyDigits(localNumber.value);
  let finalDigits = newDigits;

  /**
   * Если количество цифр не изменилось, но строка стала короче —
   * пользователь удалил символ-разделитель маски (пробел, скобку, дефис).
   * Чтобы backspace «чувствовался», убираем последнюю цифру.
   */
  if (
    newDigits.length === existingDigits.length &&
    newDigits.length > 0 &&
    raw.length < localNumber.value.length
  ) {
    finalDigits = newDigits.slice(0, -1);
  }

  const masked = applyMask(finalDigits, selectedCountry.value.mask);
  localNumber.value = masked;
  emitValue(masked);
};

const onPhoneFocus = () => {
  phoneFieldWasFocused.value = true;
};

const onPhoneBlur = (e: FocusEvent) => {
  /**
   * Не вызываем change (и не помечаем поле «тронутым») когда фокус
   * переходит внутри компонента (например, при клике на кнопку страны).
   */
  if (rootEl.value?.contains(e.relatedTarget as Node)) return;

  /**
   * Вызываем change только если пользователь хотя бы раз
   * сфокусировался на поле ввода — иначе required не будет
   * гореть до первого взаимодействия с полем.
   */
  if (!phoneFieldWasFocused.value) return;

  emit('change', buildFullNumber(localNumber.value));
};

const buildFullNumber = (local: string): string => {
  if (!local) return '';
  return `${selectedCountry.value.dialCode} ${local}`.trim();
};

const emitValue = (local: string) => {
  emit('update:modelValue', buildFullNumber(local));
};

const handleOutsideClick = (e: MouseEvent) => {
  if (rootEl.value && !rootEl.value.contains(e.target as Node)) {
    dropdownOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
});
onUnmounted(() => document.removeEventListener('click', handleOutsideClick));

watch(
  () => props.modelValue,
  (val) => {
    /**
     * Когда значение опустело — страну НЕ меняем.
     * Страну автоматически обновляем только когда во внешнем значении
     * присутствует распознаваемый код страны.
     */
    if (val) {
      const country = resolveCountryFromValue(val);
      if (country.code !== selectedCountry.value.code) {
        selectedCountry.value = country;
      }
    }

    const newLocal = getLocalPart(val ?? '', selectedCountry.value);
    if (newLocal !== localNumber.value) {
      localNumber.value = newLocal;
    }
  },
);
</script>

<style scoped lang="scss">
.phone-input-wrapper {
  @apply w-full;
}

// Новый стиль (UI-kit / hui-токены): как flat-инпут UiKitInput — плоский фон,
// прозрачная рамка, подсветка фона на фокусе; рамка danger при ошибке.
.phone-input {
  @apply flex items-stretch w-full bg-hui-default-100 border-2 border-transparent rounded-hui-md transition-colors duration-150 min-h-12 overflow-visible relative;

  &:focus-within {
    @apply bg-hui-default-200;
  }

  &--error {
    @apply bg-hui-danger/10 border-hui-danger;

    &:focus-within {
      @apply border-hui-danger;
    }
  }

  &__country {
    @apply relative flex-shrink-0;
  }

  &__country-btn {
    @apply h-full min-h-12 px-3 rounded-l-hui-md rounded-r-none font-medium text-hui-foreground;
  }

  &__flag-img {
    @apply flex-shrink-0 object-cover rounded-[2px];

    &--list {
      @apply mt-0.5;
    }
  }

  &__dial {
    @apply text-sm text-hui-foreground/80;
  }

  &__chevron {
    @apply text-xs text-hui-foreground/50;
  }

  &__divider {
    @apply w-px bg-hui-divider my-2.5 flex-shrink-0;
  }

  &__number-ui {
    @apply flex-1 min-w-0 min-h-12 h-full pl-5;
  }

  &__dropdown {
    // На всю ширину инпута: родитель .phone-input — relative, тянем left-0 right-0
    @apply absolute top-full left-0 right-0 mt-1 bg-hui-content1 border border-hui-divider rounded-hui-md shadow-lg z-50 overflow-hidden;
  }

  &__search {
    @apply w-full px-3.5 py-3 text-sm bg-transparent border-0 border-b border-hui-divider text-hui-foreground outline-none placeholder:text-hui-foreground/40 focus:border-hui-primary;
  }

  &__country-list {
    @apply max-h-64 overflow-y-auto list-none p-0 m-0;
  }

  &__country-item {
    @apply flex items-center gap-3 px-3.5 py-3 cursor-pointer text-sm text-hui-foreground hover:bg-hui-default-100;

    &--active {
      @apply bg-hui-default-200;
    }
  }

  &__country-name {
    @apply flex-1 text-hui-foreground;
  }

  &__dial-secondary {
    @apply text-hui-foreground/50 text-xs;
  }
}

.phone-input__error-wrap {
  @apply flex items-center gap-2 mt-1.5;
}

/* Перебиваем фон браузерного автозаполнения (телефон), чтобы он не светился поверх hui-темы */
.phone-input__number-ui:-webkit-autofill,
.phone-input__number-ui:-webkit-autofill:hover,
.phone-input__number-ui:-webkit-autofill:focus,
.phone-input__number-ui:-webkit-autofill:active {
  -webkit-text-fill-color: rgb(var(--hui-foreground));
  caret-color: rgb(var(--hui-foreground));
  -webkit-box-shadow: 0 0 0 1000px rgb(var(--hui-default-100)) inset;
  box-shadow: 0 0 0 1000px rgb(var(--hui-default-100)) inset;
  transition: background-color 600000s 0s, color 600000s 0s;
}
</style>
